import React, { useState } from "react";
import {
  LayoutDashboard, CalendarDays, Truck, Users, Wrench, FileText,
  Settings as SettingsIcon, CircleDollarSign, LogOut, Globe,
} from "lucide-react";
import { useAuth } from "./auth/AuthProvider.jsx";
import RequestsTab from "./requests/RequestsTab.jsx";
import { usePendingCount } from "./requests/api.js";
import EquipmentTab from "./equipment/EquipmentTab.jsx";
import CustomersTab from "./customers/CustomersTab.jsx";
import CalendarTab from "./calendar/CalendarTab.jsx";
import MaintenanceTab from "./maintenance/MaintenanceTab.jsx";
import Dashboard from "./dashboard/Dashboard.jsx";
import BooksTab from "./books/BooksTab.jsx";
import SettingsTab from "./settings/SettingsTab.jsx";
import RentalsTab from "./rentals/RentalsTab.jsx";

const APP_VERSION = "v11-dev";

const NAV_ALL = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["requests", "Requests", Globe],
  ["rentals", "Rentals", FileText],
  ["calendar", "Calendar", CalendarDays],
  ["equipment", "Equipment", Truck],
  ["customers", "Customers", Users],
  ["maintenance", "Maintenance", Wrench],
  ["books", "Books", CircleDollarSign],
  ["settings", "Settings", SettingsIcon],
];

/** Mirrors the v10 rule: books is admin-only; empty perms means full access. */
function canSee(key, profile) {
  const isAdmin = profile?.role === "admin";
  if (key === "books") return isAdmin;
  if (key === "settings") return true;
  if (isAdmin) return true;
  if (!profile?.perms || profile.perms.length === 0) return true;
  return profile.perms.includes(key);
}

export default function AppShell() {
  const { profile, isAdmin, signOut } = useAuth();
  const [tab, setTab] = useState("dashboard");
  // Drives the sidebar badge, so a new website booking is visible from any tab.
  const pendingRequests = usePendingCount();

  const nav = NAV_ALL.filter(([k]) => canSee(k, profile));
  const titles = Object.fromEntries(NAV_ALL.map(([k, l]) => [k, l]));
  const activeTab = canSee(tab, profile) ? tab : "dashboard";

  return (
    <div className="halco">
      <aside className="sb">
        <div className="sb-logo">
          <div className="wordmark">
            HAL<span>CO</span> OPS
          </div>
          <div className="sub">Equipment Rentals · North TX</div>
        </div>
        <nav className="sb-nav">
          {nav.map(([key, label, Icon]) => (
            <button
              key={key}
              className={"sb-btn" + (activeTab === key ? " on" : "")}
              onClick={() => setTab(key)}
            >
              <Icon size={17} /> {label}
              {key === "requests" && pendingRequests > 0 && (
                <span className="badge">{pendingRequests}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="sb-foot">
          <div style={{ marginBottom: 6 }}>
            {profile?.name} · {isAdmin ? "Admin" : "Staff"}
          </div>
          <button onClick={signOut}>
            <LogOut size={14} /> Sign out
          </button>
          <div style={{ marginTop: 8, fontSize: 11, color: "#6d7379" }}>
            HALCO Ops {APP_VERSION}
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="hazard" />
        <div className="topbar">
          <h1>{titles[activeTab]}</h1>
          <span className="date">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long", month: "long", day: "numeric", year: "numeric",
            })}
          </span>
        </div>
        <div className="content">
          {activeTab === "dashboard" ? (
            <Dashboard onGoToTab={setTab} />
          ) : activeTab === "requests" ? (
            <RequestsTab onGoToTab={setTab} />
          ) : activeTab === "rentals" ? (
            <RentalsTab />
          ) : activeTab === "equipment" ? (
            <EquipmentTab onGoToTab={setTab} />
          ) : activeTab === "customers" ? (
            <CustomersTab />
          ) : activeTab === "calendar" ? (
            <CalendarTab />
          ) : activeTab === "maintenance" ? (
            <MaintenanceTab />
          ) : activeTab === "books" && isAdmin ? (
            <BooksTab onGoToTab={setTab} />
          ) : activeTab === "settings" ? (
            <SettingsTab />
          ) : (
            <div className="card">
              <div className="card-h">
                <h2>{titles[activeTab]}</h2>
              </div>
              <div className="card-b">
                <div className="empty">
                  <b>Not ported yet</b>
                  This tab arrives in a later slice. See PHASE-2-PLAN.md.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
